import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  pulseTrackUpdatePermission,
  toPulseTrackDefinitionResourceRef,
} from '@internal/backstage-plugin-pulse-common';
import { Container, Header } from '@backstage/ui';
import {
  tracksDetailsSubRouteRef,
  tracksEditSubRouteRef,
  tracksNewSubRouteRef,
  tracksRouteRef,
} from '../../../../routes';
import { usePulseTracks } from '../../../../hooks';
import { EmptyState } from '../../../empty-state';
import { TrackForm } from './track-form';

/** Routed page for creating (`/create-new`) a track. */
export const PulseTrackFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getTracksPath = useRouteRef(tracksRouteRef);
  const getNewPath = useRouteRef(tracksNewSubRouteRef);
  const getEditPath = useRouteRef(tracksEditSubRouteRef);
  const getDetailsPath = useRouteRef(tracksDetailsSubRouteRef);
  const isEdit = Boolean(id);
  const { allowed: canUpdate, loading: updatePermissionLoading } =
    usePermission({
      permission: pulseTrackUpdatePermission,
      resourceRef: id ? toPulseTrackDefinitionResourceRef(id) : undefined,
    });
  const { value: tracks, loading } = usePulseTracks({ cache: !isEdit });
  const initial = id ? tracks?.find(track => track.id === id) : undefined;
  const isReadOnly = Boolean(initial && initial.source !== 'db');

  const tracksPath = getTracksPath?.() ?? '..';
  useEffect(() => {
    if (isEdit && !updatePermissionLoading && !canUpdate) {
      navigate(tracksPath, { replace: true });
    }
  }, [canUpdate, isEdit, navigate, tracksPath, updatePermissionLoading]);

  const breadcrumbHref = isEdit
    ? getEditPath?.({ id: id ?? '' }) ?? tracksPath
    : getNewPath?.() ?? tracksPath;
  // Return to wherever the form was opened from.
  const goBack = () => navigate(-1);

  let content: JSX.Element;
  if (isEdit && loading) {
    content = <Progress />;
  } else if (isEdit && !initial) {
    content = (
      <EmptyState
        title="Track not found"
        description={`No track with id "${id}" was found.`}
      />
    );
  } else {
    content = (
      <TrackForm
        key={id ?? 'new'}
        initial={initial}
        isReadOnly={isReadOnly}
        onDone={goBack}
        onCreated={createdId =>
          navigate(getDetailsPath?.({ id: createdId }) ?? tracksPath, {
            state: { pulseTrackSaved: 'created' },
          })
        }
        onUpdated={updatedId =>
          navigate(getDetailsPath?.({ id: updatedId }) ?? tracksPath, {
            state: { pulseTrackSaved: 'updated' },
          })
        }
      />
    );
  }

  return (
    <BreadcrumbEntry
      entry={{
        label: isEdit ? 'Edit track' : 'Create New',
        href: breadcrumbHref,
      }}
    >
      <Header
        title={isEdit ? 'Edit track' : 'Create track'}
        description="Define a Pulse track that groups checks and applies them to a set of catalog entities."
      />
      <Container>{content}</Container>
    </BreadcrumbEntry>
  );
};
